import React from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Box, Button, Tag, Text, Title } from '@nimbus-ds/components';
import { goTo } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { ADMIN_ROUTES, ROUTES } from 'App/Routes/constants';
import {
  IdentityAlert,
  PendingTrialPeriodAlertPage,
} from 'commons/components/IdentityAlert';
import { useBackLink, useIsMobile } from 'commons/hooks';
import { TrialPeriod } from 'commons/services/configurationService/configurationServiceResponses';
import { KycDocumentErrorsType, ValidationStatusType } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import EmptyStateFirstSectionSkeleton from './EmptyStateFirstSectionSkeleton';

interface EmptyStateFirstSectionProps {
  isNewCohort: boolean;
  isPagoNubeEnabled: boolean;
  validationStatus?: ValidationStatusType;
  validationUpdatedAt?: string;
  kycDocumentsErrors?: KycDocumentErrorsType[];
  trialPeriod?: TrialPeriod;
  blockedTrialPeriod?: boolean;
  validationStatusDetail?: string;
}

function EmptyStateFirstSection({
  isNewCohort,
  isPagoNubeEnabled,
  validationStatus,
  validationUpdatedAt,
  kycDocumentsErrors,
  trialPeriod,
  blockedTrialPeriod,
  validationStatusDetail,
}: EmptyStateFirstSectionProps): JSX.Element {
  const isMobile = useIsMobile();
  const { t } = useTranslationWithPrefix('emptyState.firstSection');
  const { push } = useHistory();
  const { setBackLink } = useBackLink();

  const trackEnableAndRedirectToPayment = async () => {
    await track('pagonube_enable_empty_click').promise;
    goTo(nexo, ADMIN_ROUTES.NUVEMPAGO_PAYMENT_METHODS);
  };

  const redirectToConfiguration = () => {
    track('pagonube_editconfig_empty_click');
    // TODO: backLinkText is not being used
    setBackLink({
      backLinkPath: ROUTES.LANDING,
      backLinkText: '',
    });
    push(ROUTES.CONFIG);
  };

  const isPendingStatus = validationStatus === 'pending';
  const isDeniedOrBlocked =
    validationStatus === 'denied' || validationStatus === 'blocked';
  const isActiveTagVisible = isPagoNubeEnabled && !isDeniedOrBlocked;
  const shouldShowIdentityAlert =
    (isPagoNubeEnabled || isDeniedOrBlocked) &&
    !!validationStatus &&
    !!validationUpdatedAt &&
    !isPendingStatus;

  const description = isPagoNubeEnabled
    ? t('descriptionActive')
    : t('descriptionInactive');

  return (
    <Box>
      {shouldShowIdentityAlert && (
        <IdentityAlert
          identityStatus={validationStatus}
          updatedAt={validationUpdatedAt}
          kycDocumentsErrors={kycDocumentsErrors ?? []}
          trialPeriod={trialPeriod}
          page={PendingTrialPeriodAlertPage.LANDING}
          blockedTrialPeriod={blockedTrialPeriod}
          statusDetail={validationStatusDetail}
        />
      )}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap="4"
        alignItems="center"
      >
        <Box flex="1" display="flex" justifyContent="flex-start">
          <img
            src="/assets/img/new-landing-arg-1.png"
            alt="Pago Nube"
            style={{ maxWidth: isMobile ? '200px' : '320px' }}
          />
        </Box>
        <Box flex="2" display="flex" flexDirection="column" gap="none">
          <Box display="flex" alignItems="center" gap="2">
            <Text color="neutral-textLow">{t('pagoNube')}</Text>
            {isActiveTagVisible && (
              <Tag appearance="success">{t('activeTag')}</Tag>
            )}
          </Box>
          <Box marginTop="2" marginBottom="4">
            <Title as={isMobile ? 'h3' : 'h1'}>{t('title')}</Title>
          </Box>
          <Text>
            <Trans>{description}</Trans>
          </Text>
          <Box display="flex" gap="4" marginTop="9">
            <Button
              appearance="primary"
              onClick={
                isNewCohort
                  ? trackEnableAndRedirectToPayment
                  : redirectToConfiguration
              }
            >
              <Text
                color="neutral-background"
                fontSize="base"
                fontWeight="medium"
              >
                {isNewCohort ? t('activate') : t('configure')}
              </Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

EmptyStateFirstSection.Skeleton = EmptyStateFirstSectionSkeleton;
export default EmptyStateFirstSection;
